// A rite is one deal to play to the Monad. The 27 rites of the lattice sit
// on the points of a 3×3×3 cube, so each is named by its point's glyph. When
// all three tablets are won, the Ternion folds them: the first rite again,
// one scale up.

import { applyMove, createState, isWon, type Deal, type Move, type Refusal, type TableState } from "./engine.ts"
import { err, ok, type Result } from "./result.ts"
import { keyOf, ringsOf, type Ring, type Rings, type Trit } from "./rings.ts"

export type OpenSym = "-" | "0" | "+"
export type RiteId = `${OpenSym}${OpenSym}${OpenSym}`

// The Ternion is named by the glyph it makes: every ring folded.
export type TernionId = "***"
export const TERNION_ID: TernionId = "***"

export type LatticeRite = {
  readonly kind: "lattice"
  readonly id: RiteId
  readonly deal: Deal
  readonly par: number
  readonly proof: readonly Move[]
}
export type TernionRite = {
  readonly kind: "ternion"
  readonly id: TernionId
  readonly deal: Deal
  readonly par: number
  readonly proof: readonly Move[]
}
export type Rite = LatticeRite | TernionRite
// A rite's name, which its best and its play in progress are kept by: one
// of the lattice's 27 rites, or the Ternion.
export type WonKey = RiteId | TernionId

const OPEN: readonly OpenSym[] = ["-", "0", "+"]
const symOfTrit = (t: Trit): OpenSym => (t === 1 ? "+" : t === -1 ? "-" : "0")

export const riteIdOf = (x: Trit, y: Trit, z: Trit): RiteId => `${symOfTrit(x)}${symOfTrit(y)}${symOfTrit(z)}`

const idsWith = (a: OpenSym, b: OpenSym): readonly RiteId[] => OPEN.map((c): RiteId => `${a}${b}${c}`)
const idsFrom = (a: OpenSym): readonly RiteId[] => OPEN.flatMap((b) => idsWith(a, b))

// All 27, inner ring first.
export const ALL_RITE_IDS: readonly RiteId[] = OPEN.flatMap(idsFrom)

const tritOf = (r: Ring): Trit => (r === "folded" ? 0 : r)

// The point of a rite as rings: x (inner), y, z (outer = the layer).
export const pointOfRite = (id: RiteId): readonly [Trit, Trit, Trit] => {
  const r: Rings = ringsOf(id)
  const [x = 0, y = 0, z = 0] = r
  return [tritOf(x), tritOf(y), tritOf(z)]
}

export const riteIdKey = (id: RiteId): string => keyOf(ringsOf(id))

export type Replay = { readonly state: TableState; readonly played: number }

// Play moves from a deal's start; stop at the first the laws refuse.
export const replay = (deal: Deal, moves: readonly Move[]): Result<{ readonly at: number; readonly refusal: Refusal }, Replay> => {
  let state = createState(deal)
  for (const [at, m] of moves.entries()) {
    const out = applyMove(state, m)
    if (out.tag === "error") return err({ at, refusal: out.error })
    state = out.value.state
  }
  return ok({ state, played: moves.length })
}

// A rite's own proof plays to the Monad, in exactly `par` moves.
export const proofHolds = (rite: Rite): boolean => {
  const r = replay(rite.deal, rite.proof)
  return r.tag === "ok" && isWon(r.value.state) && rite.proof.length === rite.par
}

// A card of fewer rings as a card of three: the rings it lacks come folded,
// inside it.
const raised = (r: Rings): Rings => (r.length === 1 ? ["folded", "folded", r[0]] : r.length === 2 ? ["folded", r[0], r[1]] : r)

// The Ternion: the three tablets, each folded into one card, laid as the
// first rite laid its three cards. The same moves win it. It ends where it
// started.
export const ternionOf = (first: LatticeRite): TernionRite => ({
  kind: "ternion",
  id: TERNION_ID,
  deal: { width: 3, altars: first.deal.altars, cols: first.deal.cols.map((c) => ({ ...c, cards: c.cards.map(raised) })) },
  par: first.par,
  proof: first.proof,
})
